import dates from './dates'
import { strings } from './'

const { toString } = strings

const hasEnrollmentGender = gender =>
  gender != null && gender !== '' && toString(gender) !== '0'

const normalizeDisabilities = disabilities => {
  if (!Array.isArray(disabilities)) return []

  return disabilities
    .map(value => Number(value))
    .filter(value => !Number.isNaN(value))
}

const buildLockedFields = (enrollment, birthdateFallback) => {
  const lockedFields = ['cpf']

  if (enrollment.name?.trim()) lockedFields.push('name')
  if (enrollment.birthdate || birthdateFallback?.trim()) {
    lockedFields.push('birthdate')
  }
  if (hasEnrollmentGender(enrollment.gender)) lockedFields.push('gender')
  if (enrollment.parentOne?.trim()) lockedFields.push('parentOne')
  if (enrollment.parentTwo?.trim()) lockedFields.push('parentTwo')

  return lockedFields
}

const enrollmentToStudent = (enrollment, cpfFallback, birthdateFallback) => {
  if (!enrollment) return {}

  const birthdate = enrollment.birthdate
    ? dates.format(enrollment.birthdate, 'L')
    : birthdateFallback || ''

  const student = {
    fromReenrollment: true,
    lockedFields: buildLockedFields(enrollment, birthdateFallback),
    cpf: enrollment.studentCpf || cpfFallback || '',
    disableCpf: true,
    name: enrollment.name || '',
    birthdate,
    gender: hasEnrollmentGender(enrollment.gender)
      ? toString(enrollment.gender)
      : '',
    parentOne: enrollment.parentOne || '',
    parentTwo: enrollment.parentTwo || '',
  }

  if (enrollment.studyPlace) {
    student.studyPlace = enrollment.studyPlace
  } else if (enrollment.school) {
    student.studyPlace = 'state'
  }

  if (typeof enrollment.disabilityAustismGiftedness === 'boolean') {
    student.hasDisability = enrollment.disabilityAustismGiftedness
    student.disabilities = enrollment.disabilityAustismGiftedness
      ? normalizeDisabilities(enrollment.disabilities)
      : []
  }

  return student
}

const newRegistrationToStudent = (cpf, birthdate) => ({
  verifiedRegistration: true,
  lockedFields: ['cpf', 'birthdate'],
  cpf: cpf || '',
  disableCpf: true,
  birthdate: birthdate || '',
})

const isReenrollmentFieldLocked = (fieldName, student) => {
  if (!(student?.fromReenrollment || student?.verifiedRegistration)) return false

  if (fieldName === 'parentOne' && student.unknownParentOne) return false
  if (fieldName === 'parentTwo' && student.unknownParentTwo) return false

  return (student.lockedFields || []).includes(fieldName)
}

const filterAnswerIds = relationships => {
  try {
    return relationships.answers.data.map(i => i.id)
  } catch (e) {
    console.error(e)
  }

  return []
}

const filterAnswers = (included, ids) => {
  try {
    return included
      .filter(i => i.type === 'answer' && ids.includes(i.id))
      .map(i => ({
        ...i.attributes,
        id: i.id,
      }))
  } catch (e) {
    console.error(e)
  }

  return []
}

const tryToFindClassification = included => {
  try {
    const { id, attributes } =
      included.find(i => i.type === 'classification') || {}

    if (id) {
      return {
        ...attributes,
        id,
      }
    }
  } catch (e) {
    console.error(e)
  }

  return {}
}

const tryToFindIntentOpt = (kind, included) => {
  try {
    const { id, attributes, relationships } =
      included.find(i => i.type === 'intent' && i.attributes.kind === kind) ||
      {}

    if (id) {
      const answerIds = filterAnswerIds(relationships)
      const answers = filterAnswers(included, answerIds)

      return {
        ...attributes,
        id,
        answers,
      }
    }
  } catch (e) {
    console.error(e)
  }

  return {}
}

const tryToFindQuizAnswers = (relationships, included) => {
  try {
    return filterAnswers(included, filterAnswerIds(relationships))
  } catch (e) {
    console.error(e)
  }

  return []
}

const fromApiToState = obj => {
  const {
    data: { attributes: i, relationships: r },
    included,
  } = obj

  const optOne = tryToFindIntentOpt('one', included)
  const optTwo = tryToFindIntentOpt('two', included)
  const optThree = tryToFindIntentOpt('three', included)

  return {
    ...i,
    classification: tryToFindClassification(included),
    student: {
      ...i,
      gender: toString(i.gender),
    },
    address: i,
    responsible: {
      name: i.rName,
      newsletter: i.rNewsletter,
      kind: i.rKind,
      cpf: i.rCpf,
      mobile: i.rMobile,
      altPhone: i.rAltPhone,
      email: i.rEmail,
    },
    intent: {
      ...i,
      optOne: {
        ...optOne,
        completed: !!optOne.id,
      },
      optTwo: {
        ...optTwo,
        completed: !!optTwo.id,
      },
      optThree: {
        ...optThree,
        completed: !!optThree.id,
      },
    },
    quiz: {
      answers: tryToFindQuizAnswers(r, included),
    },
  }
}

const subscription = {
  enrollmentToStudent,
  fromApiToState,
  isReenrollmentFieldLocked,
  newRegistrationToStudent,
}
export default subscription
